import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  LinearProgress,
  Button
} from '@mui/material';
import {
  CloudSync as CloudSyncIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  OfflineBolt as OfflineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { tileSyncManager, TileSyncStatus } from '../services/TileSyncManager';

interface TileSyncStatusIndicatorProps {
  compact?: boolean;
  showDetails?: boolean;
}

export const TileSyncStatusIndicator: React.FC<TileSyncStatusIndicatorProps> = ({ 
  compact = false, 
  showDetails = true 
}) => {
  const [syncStatus, setSyncStatus] = useState<TileSyncStatus>({
    isOnline: navigator.onLine,
    pendingChanges: 0,
    savedThisSession: false,
    lastSyncTime: null,
    syncErrors: [],
    isSyncing: false,
    batchSize: 50,
    syncInterval: 5000
  });
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  useEffect(() => {
    // Update sync status immediately
    updateSyncStatus();

    // Set up periodic updates
    const interval = setInterval(updateSyncStatus, 2000); // Update every 2 seconds for tiles

    // Set up online/offline listeners
    const handleOnline = () => updateSyncStatus();
    const handleOffline = () => updateSyncStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const updateSyncStatus = () => {
    setSyncStatus(tileSyncManager.getSyncStatus());
  };

  const handleForceSync = async () => {
    try {
      await tileSyncManager.forceSync();
      updateSyncStatus();
    } catch (error) {
      console.error('Force tile sync failed:', error);
    }
  };

  const handleClearErrors = () => {
    // Note: TileSyncManager doesn't have a clearErrors method, so we'll just update the status
    updateSyncStatus();
    setShowErrorDetails(false);
  };

  const getStatusColor = () => {
    if (!syncStatus.isOnline) return 'error';
    if (syncStatus.syncErrors.length > 0) return 'warning';
    if (syncStatus.pendingChanges > 0) return 'info';
    return 'success';
  };

  const getStatusIcon = () => {
    if (!syncStatus.isOnline) return <OfflineIcon />;
    if (syncStatus.syncErrors.length > 0) return <ErrorIcon />;
    if (syncStatus.pendingChanges > 0) return <CloudSyncIcon />;
    return <CheckCircleIcon />;
  };

  const getStatusText = () => {
    if (!syncStatus.isOnline) return 'Offline';
    if (syncStatus.isSyncing) return 'Syncing...';
    if (syncStatus.syncErrors.length > 0) return `${syncStatus.syncErrors.length} Errors`;
    if (syncStatus.pendingChanges > 0) return `${syncStatus.pendingChanges} Pending`;
    return 'Synced';
  };

  const formatLastSyncTime = () => {
    if (!syncStatus.lastSyncTime) return 'Never';
    const now = Date.now();
    const diff = now - syncStatus.lastSyncTime;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  if (compact) {
    return (
      <Tooltip title={`Tile Sync: ${getStatusText()}`}>
        {syncStatus.isSyncing ? (
          <span>
            <IconButton size="small" onClick={handleForceSync} disabled={true}>
              {getStatusIcon()}
            </IconButton>
          </span>
        ) : (
          <IconButton size="small" onClick={handleForceSync} disabled={false}>
            {getStatusIcon()}
          </IconButton>
        )}
      </Tooltip>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Main status row */}
      <Box 
        display="flex" 
        alignItems="center" 
        gap={1} 
        mb={1}
        sx={{ 
          flexWrap: 'wrap',
          minHeight: 32
        }}
      >
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            minWidth: 'fit-content',
            flexShrink: 0
          }}
        >
          Tile Sync:
        </Typography>
        
        <Chip
          icon={getStatusIcon()}
          label={getStatusText()}
          color={getStatusColor() as any}
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0 }}
        />

        {syncStatus.isOnline && syncStatus.pendingChanges > 0 && (
          <Tooltip title="Force tile sync now">
            {syncStatus.isSyncing ? (
              <span>
                <IconButton 
                  size="small" 
                  onClick={handleForceSync}
                  disabled={true}
                  sx={{ flexShrink: 0 }}
                >
                  <CloudSyncIcon />
                </IconButton>
              </span>
            ) : (
              <IconButton 
                size="small" 
                onClick={handleForceSync}
                disabled={false}
                sx={{ flexShrink: 0 }}
              >
                <CloudSyncIcon />
              </IconButton>
            )}
          </Tooltip>
        )}

        {syncStatus.lastSyncTime && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}
          >
            Last sync: {formatLastSyncTime()}
          </Typography>
        )}

        {showDetails && syncStatus.syncErrors.length > 0 && (
          <IconButton 
            size="small" 
            onClick={() => setShowErrorDetails(!showErrorDetails)}
            sx={{ flexShrink: 0 }}
          >
            {showErrorDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {/* Progress bar for syncing */}
      {syncStatus.isSyncing && (
        <LinearProgress 
          sx={{ mb: 1 }} 
          variant="indeterminate"
        />
      )}

      {/* Error section */}
      {showDetails && syncStatus.syncErrors.length > 0 && (
        <Collapse in={showErrorDetails}>
          <Alert 
            severity="warning" 
            sx={{ mb: 1 }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={handleClearErrors}
              >
                <ClearIcon />
              </IconButton>
            }
          >
            <Typography variant="body2" gutterBottom>
              {syncStatus.syncErrors.length} tile sync error(s)
            </Typography>
            
            <Box sx={{ maxHeight: 100, overflow: 'auto' }}>
              {syncStatus.syncErrors.slice(0, 3).map((error, index) => (
                <Typography key={index} variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                  {error}
                </Typography>
              ))}
              {syncStatus.syncErrors.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  ... and {syncStatus.syncErrors.length - 3} more errors
                </Typography>
              )}
            </Box>
          </Alert>
        </Collapse>
      )}

      {/* Batch info */}
      {showDetails && syncStatus.pendingChanges > 0 && (
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Batch size: {syncStatus.batchSize} | Sync interval: {syncStatus.syncInterval / 1000}s
        </Typography>
      )}
    </Box>
  );
}; 